// Owner scope for authenticated route trees (§11c). App.tsx renders every authenticated subtree inside
// <OwnerScope key={owner:epoch}>, so an account change (A → B with the same role and route) remounts it:
// component-local drafts, composer rows, thread bodies and pending sends of A can never be shown to B or
// saved under B's keys. useOwnerApi() returns request helpers bound to the epoch the component mounted
// under; once the account changes they refuse to dispatch at all (StaleOwnerError), which fences timers,
// retries and later steps of multi-step actions that outlive the component.
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { api } from './api';
import { getOwnerEpoch } from './owner';

const EpochCtx = createContext<number | null>(null);

export function OwnerScope({ children }: { children: ReactNode }) {
  const [epoch] = useState(getOwnerEpoch);
  return <EpochCtx.Provider value={epoch}>{children}</EpochCtx.Provider>;
}

type O = { idempotencyKey?: string; signal?: AbortSignal };

export function bindOwner(epoch: number) {
  return {
    epoch,
    /** false once the signed-in account has changed since this scope began. */
    alive: () => getOwnerEpoch() === epoch,
    get: <T,>(path: string, signal?: AbortSignal) => api<T>('GET', path, undefined, { signal, epoch }),
    post: <T,>(path: string, body?: unknown, o?: O) => api<T>('POST', path, body ?? {}, { ...o, epoch }),
    patch: <T,>(path: string, body: unknown, o?: O) => api<T>('PATCH', path, body, { ...o, epoch }),
    put: <T,>(path: string, body: unknown, o?: O) => api<T>('PUT', path, body, { ...o, epoch }),
    del: <T,>(path: string, o?: O) => api<T>('DELETE', path, undefined, { ...o, epoch }),
  };
}
export type OwnerApi = ReturnType<typeof bindOwner>;

/** Request helpers bound to the account this component was mounted for. */
export function useOwnerApi(): OwnerApi {
  const scoped = useContext(EpochCtx);
  const [own] = useState(getOwnerEpoch);
  const epoch = scoped ?? own;
  return useMemo(() => bindOwner(epoch), [epoch]);
}
